import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { loadAuditLog } from '../db/company';
import { selectedJobAtom, dataSourceVersionAtom, userIdAtom, userEmailCheckAtom } from '../store/company';
import { AUDIT_TABS_BY_KEY, isAuditAdmin } from '../helpers/auditTabs';
import { initialAuditStreams, loadNextAuditPage } from '../helpers/auditPaging';
import moment from 'moment';

// Smaller than the admin screen's 200 — this is a panel tucked under a grid,
// not a dedicated page, so a shorter first page keeps the open feeling instant.
const PAGE_LIMIT = 50;

const COLUMNS = [
    { key: 'when', label: 'When', w: 150 },
    { key: 'who', label: 'Who', w: 190 },
    { key: 'version', label: 'Version', w: 130 },
    { key: 'action', label: 'Action', w: 70 },
    { key: 'row', label: 'Row', w: 170 },
    { key: 'field', label: 'Field', w: 130 },
    { key: 'prev', label: 'Prev', w: 130 },
    { key: 'new', label: 'New', w: 130 },
];

const actionColor = (a) => a === 'INSERT' ? '#059669' : a === 'UPDATE' ? '#2563eb' : a === 'DELETE' ? '#dc2626' : '#64748b';

// Audit_Datetime is stored UTC (Azure SQL's GETDATE() returns UTC there), so
// force-parse as UTC and render in the viewer's zone — same as auditLog.js.
const fmtDate = (d) => d ? moment.utc(d).local().format('YYYY-MM-DD HH:mm:ss') : '';

const truncate = (s, n = 30) => {
    if (s == null) return '';
    const str = String(s);
    return str.length > n ? str.slice(0, n - 1) + '…' : str;
};

// Labels a version the way the version picker does (date, plus "WIP"), with the
// GUID appended. The GUID is not decoration: several versions routinely share a
// start date (three on Sep 11 alone), so a date on its own is ambiguous. Falls
// back to the bare GUID when the version list has not loaded or lacks the row.
const versionLabel = (guid, versionsById) => {
    if (guid == null || guid === '') return '';
    const v = versionsById.get(String(guid));
    if (!v || !v.Version_Start_DateTime) return 'v' + guid;
    const wip = String(v.Version_Type || '').includes('WIP') ? ' WIP' : '';
    return moment(v.Version_Start_DateTime).format('MMM D') + wip + ' · v' + guid;
};

/**
 * Collapsed-by-default change history for a single tab.
 *
 * `tabKey` indexes AUDIT_TABS_BY_KEY rather than taking a raw Forecast_Tab
 * string, because Labor and Equipment each span two values and the caller
 * should not have to know that.
 */
const AuditHistoryPanel = ({ tabKey, title = 'Change history' }) => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [userId] = useAtom(userIdAtom);
    const [userEmailCheck] = useAtom(userEmailCheckAtom);

    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);

    const tabs = AUDIT_TABS_BY_KEY[tabKey];

    // Nothing is requested until the panel is actually opened — these panels sit
    // on six tabs, and eagerly fetching on every tab mount would be a lot of
    // traffic for a section most users never expand.
    // Per-Forecast_Tab cursors and buffers, carried across "Load more" clicks.
    // See helpers/auditPaging.js for why each tab value is its own stream.
    const streamsRef = useRef(null);
    // Bumped on every full load. A response that arrives after the job has changed
    // belongs to the old job and must be dropped rather than rendered under the new one.
    const generationRef = useRef(0);

    const fetchRef = useRef();
    fetchRef.current = async (append = false) => {
        // Without a job the buId filter is skipped server-side, which would show
        // every job's history under this tab — misleading rather than merely empty.
        if (!tabs || !selectedJob) return;
        if (!append || !streamsRef.current) {
            generationRef.current += 1;
            streamsRef.current = initialAuditStreams(tabs);
        }
        const generation = generationRef.current;
        const job = selectedJob;
        setLoading(true);
        setError(null);

        const request = async (tab, beforeId) => {
            const res = await loadAuditLog({
                buId: job,
                // Deliberately not scoped to the selected version. Edits land on a
                // working version that is later marked Version_Type "Hidden", and
                // the version picker never lists hidden versions — so a history
                // filtered to the selected version is empty in normal use, because
                // everyone is on the newest version and nobody has edited it yet.
                // The Version column says which forecast each change landed on.
                versionGuid: null,
                // One Forecast_Tab value per request, which every deployed version
                // of readAuditLog filters on server-side.
                tab,
                beforeId,
                limit: PAGE_LIMIT,
            });
            // postRequest swallows network failures and resolves undefined, which
            // would otherwise render exactly like an empty history.
            if (res == null) throw new Error('Could not reach the audit log service.');
            if (res.error) throw new Error(res.error);
            return res.data || [];
        };

        let result;
        try {
            result = await loadNextAuditPage({ streams: streamsRef.current, pageSize: PAGE_LIMIT, request });
        } catch (error) {
            // loadNextAuditPage reports request failures itself; this only catches
            // the unexpected, so the panel can never be left spinning.
            result = { rows: [], hasMore: append, error };
        }
        if (generation !== generationRef.current) return;

        if (result.error) {
            setError(String(result.error?.message || result.error));
            if (append) {
                // Keep what already loaded, plus anything merged before the failure,
                // so a failed "Load more" never loses rows.
                setRows(prev => [...prev, ...result.rows]);
                setHasMore(true);
            } else {
                // A failed full load must not leave the previous rows on screen: after
                // a job switch they would be another job's history shown under this one.
                setRows([]);
                setHasMore(false);
                streamsRef.current = null;
            }
        } else {
            setRows(prev => append ? [...prev, ...result.rows] : result.rows);
            setHasMore(result.hasMore);
        }
        setLoading(false);
    };
    const fetchPage = useCallback((append = false) => fetchRef.current && fetchRef.current(append), []);

    // Load on first open, and refresh whenever the job changes while open — a
    // stale history from the previous job would be actively misleading. Switching
    // version no longer changes the result, so it does not trigger a refetch.
    useEffect(() => {
        if (!open) return;
        if (!selectedJob) { setRows([]); setHasMore(false); return; }
        fetchPage(false);
    }, [open, selectedJob, fetchPage]);

    const versionsById = new Map(
        (Array.isArray(versions) ? versions : []).map(v => [String(v.Version_GUID), v])
    );

    if (!isAuditAdmin(userId, userEmailCheck)) return null;
    if (!tabs) return null;

    const cell = (text, w, color, weight) => (
        <Text style={{ width: w, fontSize: 11, color: color || '#0f172a', fontWeight: weight || '400', paddingRight: 6 }}>
            {text}
        </Text>
    );

    return (
        <View style={{ margin: '16px 0', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <Pressable
                data-test={'audit-history-toggle-' + tabKey}
                onPress={() => setOpen(v => !v)}
                style={({ hovered }) => ({
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: hovered ? '#f1f5f9' : '#f8fafc',
                })}
            >
                <Text style={{ fontSize: 11, color: '#64748b', width: 10 }}>{open ? '▾' : '▸'}</Text>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>{title}</Text>
                {/* No count while errored: "0 changes" next to a failure would read as
                    a genuinely empty history, which is the confusion being fixed. */}
                {open && !loading && !error ? (
                    <Text style={{ fontSize: 11, color: '#64748b' }}>
                        {rows.length}{hasMore ? '+' : ''} change{rows.length === 1 ? '' : 's'}
                    </Text>
                ) : null}
                {loading ? <Text style={{ fontSize: 11, color: '#64748b' }}>Loading…</Text> : null}
            </Pressable>

            {open ? (
                <View>
                    {error ? (
                        <Text style={{ fontSize: 12, color: '#dc2626', padding: 12 }}>Error: {error}</Text>
                    ) : null}

                    {!error && !loading && rows.length === 0 ? (
                        <Text style={{ fontSize: 12, color: '#64748b', padding: 12 }}>
                            {selectedJob
                                ? 'No recorded changes for this tab on this job.'
                                : 'Select a job to see its change history.'}
                        </Text>
                    ) : null}

                    {rows.length > 0 ? (
                        <View style={{ overflowX: 'auto' }}>
                            <View style={{ flexDirection: 'row', backgroundColor: '#1e293b', padding: '6px 12px' }}>
                                {COLUMNS.map(c => (
                                    <Text key={c.key} style={{ width: c.w, fontSize: 10, color: '#f1f5f9', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, paddingRight: 6 }}>
                                        {c.label}
                                    </Text>
                                ))}
                            </View>
                            {rows.map((r, i) => (
                                <View
                                    key={r.Audit_ID}
                                    style={{ flexDirection: 'row', padding: '5px 12px', borderTop: '1px solid #f1f5f9', backgroundColor: i % 2 ? '#fbfdff' : '#ffffff' }}
                                >
                                    {cell(fmtDate(r.Audit_Datetime), 150, '#334155')}
                                    {cell(truncate(r.User_Name || r.User_Email || r.User_ID, 26), 190)}
                                    {cell(versionLabel(r.Version_GUID, versionsById), 130, '#475569')}
                                    {cell(r.Action_Type, 70, actionColor(r.Action_Type), '700')}
                                    {cell(truncate(r.Forecast_Row_ID, 24), 170, '#475569')}
                                    {cell(truncate(r.Forecast_Field_Name, 18), 130, '#475569')}
                                    {cell(truncate(r.Prev_Value, 18), 130, '#94a3b8')}
                                    {cell(truncate(r.New_Value, 18), 130)}
                                </View>
                            ))}
                        </View>
                    ) : null}

                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: '1px solid #f1f5f9' }}>
                        <Pressable
                            onPress={() => fetchPage(false)}
                            style={{ backgroundColor: '#e2e8f0', padding: '4px 12px', borderRadius: 4, cursor: 'pointer' }}
                        >
                            <Text style={{ color: '#334155', fontSize: 11, fontWeight: '600' }}>Refresh</Text>
                        </Pressable>
                        {hasMore ? (
                            <Pressable
                                onPress={() => fetchPage(true)}
                                style={{ backgroundColor: '#1e40af', padding: '4px 12px', borderRadius: 4, cursor: loading ? 'wait' : 'pointer' }}
                            >
                                <Text style={{ color: '#fff', fontSize: 11, fontWeight: '600' }}>Load more</Text>
                            </Pressable>
                        ) : null}
                    </View>
                </View>
            ) : null}
        </View>
    );
};

export default AuditHistoryPanel;
